import { useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { getSafeExternalHref } from '@shared/lib/utils';

import { useClassNames } from '../../../../utils/hooks';
import { Button } from '../../../Button/Button';
import { buttonStylesLink } from '../../../Button/Button.styles';
import { NavigationSection } from '../NavigationSection/NavigationSection';
import { navigationElementsStyles } from './navigationElements.styles';
import type {
  MainNavigationElementsClassNames,
  NavigationElementConfig,
  NavigationElementsProps,
  NavigationElementsStyles,
} from './types';
import { checkHasActive, getLayout, isNavExactMatch, shouldRenderNested } from './utils';

export const NavigationElements = ({ navElements, setActiveNavigationKeys }: NavigationElementsProps) => {
  const { pathname, search } = useLocation();
  const classNames = useClassNames<NavigationElementsStyles, MainNavigationElementsClassNames>(
    'NavigationElements',
    navigationElementsStyles
  );
  const getLiClassName = useCallback(
    (isActive: boolean, hasImage: boolean, disabled: boolean, hasTrailing: boolean) => {
      return [
        classNames.element,
        isActive ? 'element-active' : '',
        hasImage ? 'element-has-image' : '',
        disabled ? 'element-disabled' : '',
        hasTrailing ? 'element-has-trailing' : '',
      ]
        .join(' ')
        .trim();
    },
    [classNames.element]
  );
  const getWrapper = useCallback((element: NavigationElementConfig, children: JSX.Element) => {
    const linkProps = {
      'data-test': element.dataTest,
      className: 'element-wrapper',
    };

    if (element.isExternal && element.to) {
      const safeHref = getSafeExternalHref(element.to) || '#';
      return (
        <a
          {...linkProps}
          href={safeHref}
          target="_blank"
          rel="noopener noreferrer"
          title={typeof element.name === 'string' ? element.name : undefined}
        >
          {children}
        </a>
      );
    }
    if (!element.isExternal && element.to) {
      return (
        <NavLink {...linkProps} to={element.to}>
          {children}
        </NavLink>
      );
    }
    if (!element.isExternal && element.onClick) {
      return (
        <Button styles={buttonStylesLink} onClick={element.onClick} className="element-wrapper">
          {children}
        </Button>
      );
    }
    return <span className="element-wrapper">{children}</span>;
  }, []);
  const renderElement = useCallback(
    (element: NavigationElementConfig) => {
      const currentPath = `${pathname}${search}`;
      const isExactMatch = isNavExactMatch(element, pathname, currentPath);
      const isPartialMatch = element.to && currentPath.startsWith(element.to);

      const isActive = element.active || element.exact ? isExactMatch : isPartialMatch;
      const hasActive = checkHasActive(element, pathname);

      if (isActive || hasActive) {
        setActiveNavigationKeys([element.key]);
      }

      const liClassName = getLiClassName(
        isActive || hasActive,
        !!element.image,
        !!element.disabled,
        !!element.trailing
      );

      return (
        <li key={element.key} className={liClassName}>
          {getWrapper(element, getLayout(element, classNames))}
          {element.trailing}
          {shouldRenderNested(element.section) && (
            <NavigationSection
              config={element}
              setActiveNavigationKeys={(activeKeys: string[]) => {
                setActiveNavigationKeys([...activeKeys, element.key]);
              }}
            />
          )}
        </li>
      );
    },
    [pathname, search, setActiveNavigationKeys, classNames, getWrapper, getLiClassName]
  );

  return <ul className={classNames.root}>{navElements.map(renderElement)}</ul>;
};
